import { useMemo } from 'react';
import { useLang } from '../../lib/language';
import {
  fromNow,
  parseDraftDate,
  splitTwelveHour,
  toDateInputValue,
  toDraftValue,
  toHour24,
  withDate,
  withTime,
} from './deliveryTime';

/**
 * Delivery date + time, the simple way.
 *
 * The native `<input type="time">` was the wrong tool for a counter: its
 * segments need a keyboard to be set precisely, its clock popup differs per
 * browser, and the previous helpers fed it UTC — so the picked value visibly
 * jumped by the tenant's offset. This is three big drop-downs (hour · minute ·
 * AM/PM) a cashier can set with two taps each, quick chips for the common
 * "in half an hour" answers, and a native date input for the day only.
 *
 * `value` is the draft string (ISO instant or naive local); `onChange` always
 * receives a naive LOCAL `YYYY-MM-DDTHH:mm:00`.
 */
export function DeliveryTimePicker({
  value,
  onChange,
  minuteStep = 5,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Minute granularity of the minute drop-down (default 5). */
  minuteStep?: number;
}): JSX.Element {
  const { t } = useLang();
  const current = useMemo(() => parseDraftDate(value), [value]);
  const { hour12, minute, pm } = splitTwelveHour(current);

  // Minute options at `minuteStep`, plus the current minute when it sits off the
  // grid (a draft rehydrated from a held order), so the select never shows blank.
  const minuteOptions = useMemo(() => {
    const step = Math.max(1, Math.min(30, Math.floor(minuteStep)));
    const set = new Set<number>();
    for (let m = 0; m < 60; m += step) set.add(m);
    set.add(minute);
    return [...set].sort((a, b) => a - b);
  }, [minuteStep, minute]);

  const emit = (d: Date) => onChange(toDraftValue(d));

  const chips: Array<{ label: string; minutes: number }> = [
    { label: t({ en: 'Now', ar: 'الآن' }), minutes: 0 },
    { label: t({ en: '+30 min', ar: '+30 د' }), minutes: 30 },
    { label: t({ en: '+1 h', ar: '+1 س' }), minutes: 60 },
    { label: t({ en: '+2 h', ar: '+2 س' }), minutes: 120 },
  ];

  return (
    <div className="delivery-time-picker">
      <div className="delivery-time-quick" role="group" aria-label={t({ en: 'Quick times', ar: 'أوقات سريعة' })}>
        {chips.map((c) => (
          <button
            key={c.minutes}
            type="button"
            className="delivery-time-chip"
            onClick={() => emit(c.minutes === 0 ? new Date() : fromNow(c.minutes))}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="delivery-time-row">
        <input
          type="date"
          className="delivery-time-date"
          value={toDateInputValue(current)}
          aria-label={t({ en: 'Delivery day', ar: 'يوم التوصيل' })}
          onChange={(e) => {
            if (e.target.value) emit(withDate(current, e.target.value));
          }}
        />
        <select
          className="delivery-time-select"
          value={hour12}
          aria-label={t({ en: 'Hour', ar: 'الساعة' })}
          onChange={(e) => emit(withTime(current, toHour24(Number(e.target.value), pm), minute))}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, '0')}
            </option>
          ))}
        </select>
        <span className="delivery-time-colon" aria-hidden="true">:</span>
        <select
          className="delivery-time-select"
          value={minute}
          aria-label={t({ en: 'Minute', ar: 'الدقيقة' })}
          onChange={(e) => emit(withTime(current, toHour24(hour12, pm), Number(e.target.value)))}
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
        <div className="delivery-time-ampm" role="group" aria-label={t({ en: 'AM or PM', ar: 'صباحاً أو مساءً' })}>
          <button
            type="button"
            className={`delivery-time-ampm-btn ${!pm ? 'is-on' : ''}`}
            aria-pressed={!pm}
            onClick={() => emit(withTime(current, toHour24(hour12, false), minute))}
          >
            {t({ en: 'AM', ar: 'ص' })}
          </button>
          <button
            type="button"
            className={`delivery-time-ampm-btn ${pm ? 'is-on' : ''}`}
            aria-pressed={pm}
            onClick={() => emit(withTime(current, toHour24(hour12, true), minute))}
          >
            {t({ en: 'PM', ar: 'م' })}
          </button>
        </div>
      </div>
    </div>
  );
}
